import { getFileExtension } from "../../../util/getFileExtension";

const BASE_FILE_EXTENSIONS = ["pdf", "docx", "doc"];
const TXT_FILE_EXTENSIONS = ["txt"];
const RTF_FILE_EXTENSIONS = ["rtf"];
const MAX_FILE_NAME_CHARS = 90;
const BYTE = 1;
const KILO_BYTE = 1024;

export const MIN_FILE_SIZE_MAP = {
  pdf: KILO_BYTE,
  txt: 162 * BYTE,
  docx: 4 * KILO_BYTE,
  doc: 10 * KILO_BYTE,
  rtf: 476 * BYTE,
};
export const MAX_UPLOAD_MEGABYTES = 5 * KILO_BYTE * 1024;

export enum FileInputValidity {
  INVALID_EXTENSION = "INVALID_EXTENSION",
  INVALID_FILENAME_TOO_LARGE = "INVALID_FILENAME_TOO_LARGE",
  INVALID_SIZE_TOO_LARGE = "INVALID_SIZE_TOO_LARGE",
  INVALID_SIZE_TOO_SMALL = "INVALID_SIZE_TOO_SMALL",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
  VALID = "VALID",
}

export interface ValidateFileOptions {
  /** Allow .txt uploads. Default false (pdf/docx/doc only). */
  allowTxtFiles?: boolean;
  /** Allow .rtf uploads (files that may fail server-side parsing). Default false. */
  allowUnparsableFiles?: boolean;
}

export const isInvalidFileExtension = (
  file: { name: string },
  { allowTxtFiles = false, allowUnparsableFiles = false }: ValidateFileOptions = {},
): boolean => {
  const extension = getFileExtension(file.name);
  if (BASE_FILE_EXTENSIONS.includes(extension)) return false;
  if (allowTxtFiles && TXT_FILE_EXTENSIONS.includes(extension)) return false;
  if (allowUnparsableFiles && RTF_FILE_EXTENSIONS.includes(extension)) return false;
  return true;
};

export const isInvalidFileName = (file: { name: string }): boolean => {
  return file.name.length > MAX_FILE_NAME_CHARS;
};

export const isFileTooLarge = (file: { size: number }): boolean => {
  return file.size > MAX_UPLOAD_MEGABYTES;
};

export const isFileTooSmall = (file: { name: string; size: number }): boolean => {
  const extension = getFileExtension(file.name);
  const minFileSize = Number(MIN_FILE_SIZE_MAP[extension as keyof typeof MIN_FILE_SIZE_MAP]);
  return file.size < minFileSize;
};

export function validateSelectedFile(
  file: { name: string; size: number },
  options: ValidateFileOptions = {},
): FileInputValidity {
  // Error if file type invalid
  if (isInvalidFileExtension(file, options)) {
    return FileInputValidity.INVALID_EXTENSION;
  }

  // Error if file name length > 90 characters
  if (isInvalidFileName(file)) {
    return FileInputValidity.INVALID_FILENAME_TOO_LARGE;
  }

  // Error if file size is > 5MB
  if (isFileTooLarge(file)) {
    return FileInputValidity.INVALID_SIZE_TOO_LARGE;
  }

  // Error if file size is too small
  if (isFileTooSmall(file)) {
    return FileInputValidity.INVALID_SIZE_TOO_SMALL;
  }

  return FileInputValidity.VALID;
}

export type UploadError = {
  title: string;
  message: string;
};

export const getUploadErrorTitle = (
  validity: FileInputValidity,
  gettext: (message: string) => string,
): string => {
  switch (validity) {
    case FileInputValidity.INVALID_FILENAME_TOO_LARGE:
      return gettext("File name is too long");
    case FileInputValidity.INVALID_EXTENSION:
      return gettext("Invalid resume file type");
    case FileInputValidity.INVALID_SIZE_TOO_LARGE:
      return gettext("Uploaded file is too big");
    case FileInputValidity.INVALID_SIZE_TOO_SMALL:
      return gettext("Resume didn't upload");
    case FileInputValidity.UNKNOWN_ERROR:
      return gettext("");
    case FileInputValidity.VALID:
    default:
      return "";
  }
};

export const getUploadErrorMessage = (
  validity: FileInputValidity,
  gettext: (message: string) => string,
  {
    allowTxtFiles = false,
    allowUnparsableFiles = false,
    fileName,
  }: ValidateFileOptions & { fileName?: string } = {},
): string => {
  switch (validity) {
    case FileInputValidity.INVALID_FILENAME_TOO_LARGE:
      return gettext(
        "The file name must be no longer than 90 characters. Please select a different file to upload.",
      );
    case FileInputValidity.INVALID_EXTENSION:
      if (allowUnparsableFiles) {
        return gettext(
          "Try pdf, docx, doc, txt, or rtf. Please select a different file to upload.",
        );
      }
      if (allowTxtFiles) {
        return gettext("Try pdf, docx, doc, or txt. Please select a different file to upload.");
      }
      return gettext("Try pdf, docx, or doc. Please select a different file to upload.");
    case FileInputValidity.INVALID_SIZE_TOO_LARGE:
      return gettext(
        "Documents must be smaller than 5MB. Please select a different file to upload.",
      );
    case FileInputValidity.INVALID_SIZE_TOO_SMALL:
      switch (fileName ? getFileExtension(fileName) : undefined) {
        case "pdf":
          return gettext("Upload a PDF file larger than 1 KB.");
        case "docx":
          return gettext("Upload a .docx file larger than 4 KB.");
        case "doc":
          return gettext("Upload a .doc file larger than 10 KB.");
      }
      // Fallback: fileName missing/unknown extension, or legacy flags (allowTxtFiles / allowUnparsableFiles) active.
      if (allowUnparsableFiles) {
        return gettext(
          "Documents must be larger than 1KB for .pdf, 162B for .txt, 4KB for .docx, 10KB for .doc, and 476B for .rtf. Please select a different file to upload.",
        );
      }
      if (allowTxtFiles) {
        return gettext(
          "Documents must be larger than 1KB for .pdf, 162B for .txt, 4KB for .docx, and 10KB for .doc. Please select a different file to upload.",
        );
      }
      return gettext(
        "Documents must be larger than 1KB for .pdf, 4KB for .docx, and 10KB for .doc. Please select a different file to upload.",
      );
    case FileInputValidity.UNKNOWN_ERROR:
      return gettext("There was an error uploading your resume. Please try again.");
    case FileInputValidity.VALID:
    default:
      return "";
  }
};

export const getUploadError = (
  validity: FileInputValidity,
  gettext: (message: string) => string,
  options: ValidateFileOptions & { fileName?: string } = {},
): UploadError => ({
  title: getUploadErrorTitle(validity, gettext),
  message: getUploadErrorMessage(validity, gettext, options),
});

export const updateMediaType = (file: File, newMediaType: string): File => {
  return new File([file], file.name, { type: newMediaType });
};
